/**
 * Scheduled Task Entity Schemas
 *
 * Types for scheduled tasks managed through the backend API.
 * Task configuration is stored in the database, synced to EventBridge.
 */

import { z } from 'zod'

/**
 * HTTP methods supported by scheduled task endpoints
 */
export const ScheduledTaskHttpMethodSchema = z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])
export type ScheduledTaskHttpMethod = z.infer<typeof ScheduledTaskHttpMethodSchema>

/**
 * Endpoint configuration for a scheduled task
 */
export const ScheduledTaskEndpointSchema = z.object({
  path: z.string(),
  method: ScheduledTaskHttpMethodSchema,
  body: z.record(z.string(), z.unknown()).optional(),
  queryParams: z.record(z.string(), z.string()).optional()
})

export type ScheduledTaskEndpoint = z.infer<typeof ScheduledTaskEndpointSchema>

/**
 * Which domain a scheduled task's work belongs to (R11, R12). Closed on
 * purpose: the admin surface's filter and default sort (R14, U7) key on this
 * string, so a domain added by a typo would file a task nothing groups it
 * with. `observatory` is the sync-jobs observer's own task; `events` is the
 * ticketed-events domain, whose first member is the recurring signup sheets'
 * slot-minting job (plan 2026-09-18-0147, KTD3).
 *
 * Mirrors `ScheduledTask.domain`, a `VarChar` column and not a Prisma enum
 * (KD5, KTD6) — see the comment on that column in
 * `apps/backend/prisma/schema/system.prisma`, same convention as
 * `AgentRun.lane`.
 */
export const SCHEDULED_TASK_DOMAINS = [
  'sync',
  'stream',
  'media',
  'retention',
  'revenue',
  'infra',
  'observatory',
  'events'
] as const

export const ScheduledTaskDomainSchema = z.enum(SCHEDULED_TASK_DOMAINS)
export type ScheduledTaskDomain = z.infer<typeof ScheduledTaskDomainSchema>

export function isScheduledTaskDomain(value: string): value is ScheduledTaskDomain {
  return (SCHEDULED_TASK_DOMAINS as readonly string[]).includes(value)
}

/**
 * Scheduled Task Schema
 * Represents a scheduled task stored in the database and synced to EventBridge
 */
export const ScheduledTaskSchema = z.object({
  name: z.string(),
  description: z.string(),
  schedule: z.string(),
  enabled: z.boolean(),
  endpoint: ScheduledTaskEndpointSchema,
  /**
   * Optional and nullable: pre-backfill rows (U2 has not run yet, or has not
   * reached this row) have no domain, and readers must not break on them.
   */
  domain: ScheduledTaskDomainSchema.nullable().optional()
})

export type ScheduledTask = z.infer<typeof ScheduledTaskSchema>

/**
 * Execution status enum. `skipped` (KTD4) is a tick that yielded to its own
 * overlap guard (R3, R15) — distinct from `failed`, which would otherwise
 * false-alarm on ordinary overlap, and from `completed`, which every reader
 * treats as ran-and-succeeded.
 */
export const ScheduledTaskExecutionStatusSchema = z.enum(['queued', 'processing', 'completed', 'failed', 'skipped'])
export type ScheduledTaskExecutionStatus = z.infer<typeof ScheduledTaskExecutionStatusSchema>

/**
 * A single scheduled task execution entry
 */
export const ScheduledTaskExecutionSchema = z.object({
  executionId: z.string(),
  taskName: z.string(),
  status: ScheduledTaskExecutionStatusSchema,
  triggerSource: z.string().optional(),
  durationMs: z.number().optional(),
  summary: z.string().optional(),
  errorMessage: z.string().optional(),
  result: z.unknown().optional(),
  queuedAt: z.string(),
  startedAt: z.string().optional(),
  completedAt: z.string().optional()
})

export type ScheduledTaskExecution = z.infer<typeof ScheduledTaskExecutionSchema>
